import {
  CheckmarkFilled,
  CircleDash,
  CloseFilled,
  InProgress,
} from '@carbon/react/icons';
import { DefinitionTooltip } from '@carbon/react';
import classNames from 'classnames';
import React from 'react';
import style from './rollup-verification-status.module.scss';

interface RollupVerificationStatusProps {
  totalSteps: number;
  markedCorrectTotal: number;
  markedIncorrectTotal: number;
  className?: string;
  /**
   * Position the tooltip with floating-ui (`position: fixed`) so it escapes an
   * ancestor's `overflow: hidden`. Needed inside the eval3-new DataTable, whose
   * cells clip; legacy tables don't pass this and keep the default CSS alignment.
   */
  autoAlign?: boolean;
}

const RollupVerificationStatus: React.FC<RollupVerificationStatusProps> = ({
  totalSteps,
  markedCorrectTotal,
  markedIncorrectTotal,
  className,
  autoAlign = false,
}) => {
  const getStatus = () => {
    const markedTotal = markedCorrectTotal + markedIncorrectTotal;

    // If all marked correct
    if (
      markedIncorrectTotal === 0 &&
      markedCorrectTotal === totalSteps &&
      totalSteps > 0
    ) {
      return 'all';
    }

    // If any marked incorrect
    if (markedIncorrectTotal > 0) {
      return 'incorrect';
    }

    // If only totalSteps given and markedCorrect + markedIncorrect are 0
    if (markedTotal === 0 && totalSteps > 0) {
      return 'none';
    }

    // When totalSteps > (markedCorrect + markedIncorrect)
    if (totalSteps > markedTotal) {
      return 'partial';
    }

    return 'none';
  };

  const status = getStatus();
  const markedTotal = markedCorrectTotal + markedIncorrectTotal;

  const getTooltipText = () => {
    if (status === 'all') {
      return (
        <>
          {markedCorrectTotal} of {totalSteps} NRIs reviewed
          <br />
          {markedCorrectTotal} marked correct
        </>
      );
    }
    if (status === 'incorrect') {
      return (
        <>
          {markedTotal} of {totalSteps} NRIs reviewed
          <br />
          {markedIncorrectTotal} marked incorrect
          {markedCorrectTotal > 0 && (
            <>
              <br />
              {markedCorrectTotal} marked correct
            </>
          )}
        </>
      );
    }
    if (status === 'partial') {
      return (
        <>
          {markedTotal} of {totalSteps} NRIs reviewed
          {markedCorrectTotal > 0 && (
            <>
              <br />
              {markedCorrectTotal} marked correct
            </>
          )}
          {markedIncorrectTotal > 0 && (
            <>
              <br />
              {markedIncorrectTotal} marked incorrect
            </>
          )}
        </>
      );
    }
    // none
    return 'No NRIs have been reviewed yet.';
  };

  const tooltipText = getTooltipText();

  const renderIcon = () => {
    let iconClassName: string;
    switch (status) {
      case 'all':
        iconClassName = style.allVerified;
        return <CheckmarkFilled className={iconClassName} />;
      case 'incorrect':
        iconClassName = style.incorrectVerified;
        return <CloseFilled className={iconClassName} />;
      case 'partial':
        iconClassName = style.partialVerified;
        return <InProgress className={iconClassName} />;
      case 'none':
      default:
        iconClassName = style.noneVerified;
        return <CircleDash className={iconClassName} />;
    }
  };

  return (
    <DefinitionTooltip
      definition={tooltipText}
      align="top"
      autoAlign={autoAlign}
      className={classNames(style.rollupVerificationTooltip, className)}
    >
      {renderIcon()}
    </DefinitionTooltip>
  );
};

export default RollupVerificationStatus;
